import React, { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';

// Simple CSS Spinner
const Spinner = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
);

const CustomForm = ({ apiUrl }: any) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    stateAndInstructions: '',
    selectedService: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [controls, inView]);

  const variants = {
    visible: { opacity: 1, translateY: 0, transition: { duration: 0.6 } },
    hidden: { opacity: 0, translateY: 20 },
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const sendEmail = async (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);
    const templateParams = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      message: formData.stateAndInstructions,
      service: formData.selectedService,
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_SERVICE_ID!,
        process.env.REACT_APP_TEMPLATE_ID!,
        templateParams,
        process.env.REACT_APP_PUBLIC_KEY!
      );
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        stateAndInstructions: '',
        selectedService: '',
      });
    } catch (error) {
      console.error('Failed to send email:', error);
    }
    setIsSubmitting(false);
  };

  const debouncedSendEmail = debounce(sendEmail, 1000);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variants}
      className="max-w-md mx-auto my-10 p-6 pb-10 bg-white rounded"
    >
      <form
        onSubmit={(e: any) => {
          e.persist(); // This line allows the event to be used in an async function
          e.preventDefault(); // Prevent the default form submission behavior
          debouncedSendEmail(e);
        }}
      >
        <h2 className="text-lg font-bold mb-4">Start Your Service Here!</h2>
        {/* Form inputs remain unchanged */}
        <div className="mb-4">
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700"
          >
            First name *
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 bg-green-100 border border-gray-300 rounded-md shadow-sm focus:outline-none"
            required
          />
        </div>
        <div className="mb-4">
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700"
          >
            Last name *
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 bg-green-100 border border-gray-300 rounded-md shadow-sm focus:outline-none"
            required
          />
        </div>
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 bg-green-100 border border-gray-300 rounded-md shadow-sm focus:outline-none"
            required
          />
        </div>
        <div className="mb-4">
          <label
            htmlFor="stateAndInstructions"
            className="block text-sm font-medium text-gray-700"
          >
            Message *
          </label>
          <textarea
            id="stateAndInstructions"
            name="stateAndInstructions"
            value={formData.stateAndInstructions}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 bg-green-100 border border-gray-300 rounded-md shadow-sm focus:outline-none"
            required
          ></textarea>
        </div>
        <div className="mb-4">
          <label
            htmlFor="selectedService"
            className="block text-sm font-medium text-gray-700"
          >
            Select a service *
          </label>
          <select
            id="selectedService"
            name="selectedService"
            value={formData.selectedService}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 bg-green-100 border border-gray-300 rounded-md shadow-sm focus:outline-none"
            required
          >
            <option value="">Select service</option>
            <option value="Not Sure? No Problem! - $0.00">
              Not Sure? No Problem! - $0.00
            </option>
            <option value="Gold LLC Package - $900.00">
              Gold LLC Package - $900.00
            </option>
            <option value="Complete LLC Package - $500.00">
              Complete LLC Package - $500.00
            </option>
            <option value="U.S. Trademark Registration - $1000.00">
              U.S. Trademark Registration - $1000.00
            </option>
            <option value="The Business Credit Pathway (Standard) - $900.00">
              The Business Credit Pathway (Standard) - $900.00
            </option>
            <option value="The Business Credit Pathway (Premier) - $2000.00">
              The Business Credit Pathway (Premier) - $2000.00
            </option>
          </select>
        </div>
        <button
          type="submit"
          className={`w-full ${
            isSubmitted
              ? 'bg-green-200 text-slate-700'
              : 'bg-green-600 text-white'
          } hover:bg-green-700 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline relative`}
          disabled={isSubmitting || isSubmitted}
        >
          {isSubmitting ? (
            <div className="w-full flex justify-center items-center">
              <Spinner />
            </div>
          ) : isSubmitted ? (
            'Submitted'
          ) : (
            'Send Message'
          )}
        </button>
      </form>
    </motion.div>
  );
};

export default CustomForm;
